import React, { useState, useEffect } from "react";
import {
    FaEdit,
    FaCheckCircle,
    FaHourglassStart,
    FaPlay,
    FaCheckDouble,
    FaTimesCircle,
    FaBullhorn,
    FaArrowRight,
    FaChartLine
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const FreeAdsDashboard = () => {
    const navigate = useNavigate();
    // Placeholder stats - can be connected to API later
    const [stats, setStats] = useState({
        pending: 12,
        approved: 45,
        running: 8,
        completed: 156,
    });

    const menuItems = [
        {
            title: "Pending",
            count: stats.pending,
            icon: FaHourglassStart,
            color: "from-yellow-400 to-orange-500",
            lightColor: "bg-yellow-50 text-yellow-600",
            path: "/admin/freependingads",
            description: "Review requests",
            btnText: "Review"
        },
        {
            title: "Running",
            count: stats.running,
            icon: FaPlay,
            color: "from-blue-400 to-indigo-500",
            lightColor: "bg-blue-50 text-blue-600",
            path: "/admin/freerunningads",
            description: "Active ads",
            btnText: "View"
        },
        {
            title: "Approved",
            count: stats.approved,
            icon: FaCheckCircle,
            color: "from-green-400 to-emerald-500",
            lightColor: "bg-green-50 text-green-600",
            path: "/admin/freeapprovedads",
            description: "Published ads",
            btnText: "View"
        },
        {
            title: "History",
            count: stats.completed,
            icon: FaCheckDouble,
            color: "from-purple-400 to-pink-500",
            lightColor: "bg-purple-50 text-purple-600",
            path: "/admin/freecompletedads",
            description: "Past archive",
            btnText: "History"
        },
        {
            title: "Create Ad",
            count: null,
            icon: FaEdit,
            color: "from-rose-400 to-red-500",
            lightColor: "bg-rose-50 text-rose-600",
            path: "/admin/createfreeads",
            description: "Launch new ad",
            btnText: "Create"
        },
        {
            title: "Modify Ads",
            count: null,
            icon: FaEdit,
            color: "from-gray-400 to-gray-500",
            lightColor: "bg-gray-50 text-gray-600",
            path: "/admin/free-ads/modify",
            description: "Edit existings",
            btnText: "Modify"
        }
    ];

    return (
        <div className="min-h-screen bg-slate-50/50 p-3 sm:p-6 lg:p-10">
            {/* Premium Header */}
            <div className="max-w-7xl mx-auto mb-8 sm:mb-12">
                <div className="bg-gradient-to-br from-indigo-700 via-violet-800 to-purple-900 rounded-[2.5rem] p-6 sm:p-14 text-white shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-48 -mt-48" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-2xl -ml-32 -mb-32" />

                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 sm:p-3 bg-white/15 backdrop-blur-md rounded-2xl shadow-inner border border-white/10">
                                <FaBullhorn className="text-xl sm:text-2xl text-indigo-200" />
                            </div>
                            <span className="font-black text-indigo-100 uppercase tracking-[0.3em] text-[10px] sm:text-xs opacity-80">Portal Administration</span>
                        </div>
                        <h1 className="text-3xl sm:text-6xl font-black tracking-tighter mb-4 uppercase">Free Ads Control</h1>
                        <p className="text-indigo-100 max-w-2xl text-sm sm:text-xl font-medium opacity-70 leading-relaxed">
                            Orchestrate your campaign ecosystem. Validate requests, monitor live performance, and analyze historical engagement data.
                        </p>
                    </div>
                </div>
            </div>

            {/* Navigation Grid */}
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-8">
                    <div className="w-1.5 h-6 bg-indigo-600 rounded-full" />
                    <h2 className="text-slate-400 font-black uppercase tracking-[0.2em] text-xs sm:text-sm text-center">Campaign Management HUB</h2>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
                    {menuItems.map((item, index) => (
                        <div
                            key={index}
                            onClick={() => navigate(item.path)}
                            className="bg-white rounded-[2rem] p-4 sm:p-8 shadow-xl shadow-slate-200/50 border border-slate-100 hover:border-indigo-200 hover:shadow-2xl transition-all duration-500 cursor-pointer group relative overflow-hidden flex flex-col items-center text-center active:scale-95"
                        >
                            <div className={`absolute top-0 right-0 w-20 h-20 sm:w-32 sm:h-32 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 rounded-bl-full transition-all duration-500 group-hover:scale-150`} />

                            <div className="mb-4 sm:mb-6 relative">
                                <div className={`w-12 h-12 sm:w-20 sm:h-20 rounded-[1.5rem] sm:rounded-[2rem] flex items-center justify-center ${item.lightColor} group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 shadow-sm`}>
                                    <item.icon className="text-xl sm:text-3xl" />
                                </div>
                                {item.count !== null && (
                                    <div className="absolute -top-2 -right-2 bg-slate-900 text-white text-[10px] sm:text-xs font-black w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                                        {item.count}
                                    </div>
                                )}
                            </div>

                            <div className="relative">
                                <h3 className="text-xs sm:text-lg font-black text-slate-800 mb-1 group-hover:text-indigo-600 transition-colors uppercase tracking-tight">
                                    {item.title}
                                </h3>
                                <p className="hidden sm:block text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {item.btnText} Portal
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Analytics & Support */}
            <div className="max-w-7xl mx-auto mt-12 sm:mt-20 grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-10">
                <div className="lg:col-span-2 bg-slate-900 rounded-[2.5rem] p-8 sm:p-14 text-white relative overflow-hidden shadow-2xl group border border-slate-800">
                    <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-6">
                            <FaChartLine className="text-indigo-500 text-2xl" />
                            <h3 className="text-2xl sm:text-4xl font-black uppercase tracking-tight">Ecosystem Insights</h3>
                        </div>
                        <p className="text-slate-400 text-sm sm:text-xl font-medium mb-10 max-w-xl leading-relaxed">
                            Deep dive into campaign conversions, user activity heatmaps, and geographic distribution metrics.
                        </p>
                        <button className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 rounded-2xl font-black transition-all flex items-center gap-3 text-xs sm:text-sm shadow-xl shadow-indigo-900/50 uppercase tracking-widest group/btn active:scale-95">
                            Access Full Analytics <FaArrowRight className="group-hover/btn:translate-x-2 transition-transform" />
                        </button>
                    </div>
                    <div className="absolute right-0 bottom-0 w-64 h-64 sm:w-[500px] sm:h-[500px] bg-indigo-600/10 rounded-full filter blur-3xl opacity-50 transform translate-x-1/4 translate-y-1/4 group-hover:scale-110 transition-transform duration-700" />
                </div>

                <div className="bg-white rounded-[2.5rem] p-8 sm:p-14 border border-slate-100 shadow-xl shadow-slate-200/50 flex flex-col justify-center items-center text-center group hover:border-orange-200 transition-colors">
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-orange-50 rounded-[2rem] flex items-center justify-center mb-6 text-orange-500 shadow-inner group-hover:scale-110 transition-transform duration-500">
                        <FaBullhorn className="text-2xl sm:text-4xl" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-black text-slate-800 mb-2 uppercase tracking-tight">Policy Hub</h3>
                    <p className="text-slate-400 text-xs sm:text-sm font-bold uppercase tracking-widest mb-10 opacity-70">
                        Review community standards and approval protocols.
                    </p>
                    <button className="text-indigo-600 font-black text-xs sm:text-sm hover:text-indigo-800 transition-colors uppercase tracking-[0.3em] flex items-center gap-2 group/link">
                        READ DOCUMENTATION <FaArrowRight className="text-[10px] group-hover/link:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FreeAdsDashboard;
